/**
 * Redux State Size Monitor
 *
 * A utility to monitor and detect abnormal Redux state size growth patterns.
 * Uses adaptive baselines to work consistently across environments and devices.
 *
 * Features:
 * - Automatic baseline establishment based on initial state
 * - Context-aware size expectations based on slice content
 * - Detection of abnormal growth patterns
 * - Detailed analysis of problematic state areas
 * - Works consistently in both development and production
 * - Selective warning display: only show critical issues by default
 *
 * USAGE MODES:
 * By default, only critical Redux state issues will be displayed in the console to
 * reduce noise for regular developers (CRITICAL_WARNINGS_ONLY=true). To see all
 * warnings for performance debugging, set REDUX_SHOW_ALL_WARNINGS=true in your
 * .env file. All issues are still logged and tracked regardless of this setting.
 *
 * CRITICAL ISSUES are defined as:
 * - Growth exceeding 1000% in a short period
 * - State exceeding 1.6MB total (80% of absolute limit)
 * - Any slice exceeding 150% of its expected maximum
 * - Extremely rapid growth in any slice (>1000%)
 *
 * TECHNICAL DEBT NOTE:
 * Current thresholds have been calibrated based on observed app behavior (April 2025):
 * - Total state: ~2 MB (absolute maximum warning threshold)
 * - API slice: ~700 KB (increased from 400 KB after observing normal usage)
 * - Songs slice: ~400 KB
 * - Auth slice: ~10 KB
 *
 * NOTE (April 2025): We've adjusted these thresholds based on actual usage data
 * with our media-centric application. Testing revealed that the API slice regularly
 * reaches ~470KB during normal operation, and state growth of 300-400% is common
 * when users interact with media features (changing playlists, toggling waveforms).
 * These patterns do not impact user experience and appear to be normal for our app.
 *
 * CRITICAL: These threshold adjustments only modify configuration values and
 * do not alter startup timing, initialization sequence, or any other timing-sensitive
 * code. This ensures we avoid introducing race conditions or startup issues.
 *
 * TODO: Consider implementing these optimizations if performance issues arise:
 * 1. Implement pagination/virtualization for songs instead of storing all in Redux
 * 2. Add cache expiration for API slice
 * 3. Review auth slice data structure
 */

import type { Store } from "@reduxjs/toolkit";

import rootLogger from "util/logger";

import { MonitoringConfig } from "./monitoringConfig";

const logger = rootLogger.getLogger("reduxState");

// Configuration - can be adjusted if needed
const CONFIG = {
  // How often to check state size (milliseconds)
  CHECK_INTERVAL: 60000, // 1 minute

  // How long to wait for initial baseline (milliseconds)
  INITIAL_BASELINE_DELAY: 5000, // 5 seconds

  // Threshold for significant relative growth (e.g., 0.3 = 30%)
  // Increased to 150% based on observed normal growth patterns in our media app
  SIGNIFICANT_GROWTH_THRESHOLD: 1.5, // 150% growth (increased from 30%)

  // Threshold for rapid growth that may indicate issues
  // Increased to 500% based on observed spikes during normal media operations
  RAPID_GROWTH_THRESHOLD: 5.0, // 500% growth (increased from 50%)

  // Absolute size that should be flagged regardless of baselines (bytes)
  ABSOLUTE_SIZE_WARNING: 2 * 1024 * 1024, // 2 MB - maintained as upper limit

  // Maximum allowed measurements history to keep
  MAX_HISTORY_ITEMS: 10,

  // Key to use for persisting baselines in localStorage
  STORAGE_KEY: "redux_state_size_baselines",

  // How often to persist baselines (milliseconds)
  PERSIST_INTERVAL: 5 * 60 * 1000, // 5 minutes

  // Maximum age of persisted baselines before they're considered stale (milliseconds)
  MAX_BASELINE_AGE: 7 * 24 * 60 * 60 * 1000, // 7 days

  // Version for persisted baselines (increment when thresholds change significantly)
  BASELINE_VERSION: 3, // Increased after April 2025 threshold calibration

  // Only show critical Redux state warnings (vs. all warnings)
  // Set environment variable REDUX_SHOW_ALL_WARNINGS=true to see all warnings
  CRITICAL_WARNINGS_ONLY:
    typeof process !== "undefined" &&
    process.env.REDUX_SHOW_ALL_WARNINGS !== "true",
};

// Interface for size measurements
interface SizeMeasurement {
  timestamp: number;
  totalSize: number;
  sliceSizes: Record<string, number>;
}

// Interface for the monitoring metadata
interface MonitoringState {
  // Whether initial baselines have been established
  baselineEstablished: boolean;

  // Timestamp of when baselines were established
  baselineTimestamp: number;

  // Initial state size when baselines were established
  initialStateSize: number;

  // Expected maximum state size based on initial size and content
  maxExpectedStateSize: number;

  // Per-slice initial sizes
  initialSliceSizes: Record<string, number>;

  // Per-slice expected maximum sizes
  maxExpectedSliceSizes: Record<string, number>;

  // History of recent measurements for trend analysis
  measurementHistory: SizeMeasurement[];

  // Last time we persisted the baselines
  lastPersistTime: number;

  // Handler for the interval timer
  intervalHandler: number | null;
}

// Singleton instance of the monitoring state
let monitoringState: MonitoringState = {
  baselineEstablished: false,
  baselineTimestamp: 0,
  initialStateSize: 0,
  maxExpectedStateSize: 0,
  initialSliceSizes: {},
  maxExpectedSliceSizes: {},
  measurementHistory: [],
  lastPersistTime: 0,
  intervalHandler: null,
};

/**
 * Measures the size of a JavaScript object by serializing it to JSON
 * @param obj Any serializable JavaScript object
 * @returns Size in bytes
 */
function measureObjectSize(obj: any): number {
  try {
    const json = JSON.stringify(obj);
    return json.length;
  } catch (e) {
    // If serialization fails, make a best effort estimate
    logger.warn("Failed to serialize object for size measurement", e);
    return estimateObjectSize(obj);
  }
}

/**
 * Fallback function to estimate object size when serialization fails
 * @param obj Any JavaScript object
 * @returns Estimated size in bytes
 */
function estimateObjectSize(obj: any): number {
  if (obj === null || obj === undefined) return 0;
  if (typeof obj !== "object") return 8; // Primitive approximate size

  // Recursive estimation
  let total = 0;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Add key size
      total += key.length * 2; // UTF-16 chars

      // Add value size
      const value = obj[key];
      if (typeof value === "object" && value !== null) {
        total += estimateObjectSize(value);
      } else if (typeof value === "string") {
        total += value.length * 2; // UTF-16 chars
      } else {
        total += 8; // Primitive approximate size
      }
    }
  }

  return total;
}

/**
 * Format a size in bytes to a human-readable string
 * @param bytes Size in bytes
 * @returns Formatted string (e.g., "1.5 MB")
 */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Estimate a reasonable maximum expected size for a state slice based on its name and content
 * @param sliceName The name of the Redux slice
 * @param initialSize The initial measured size of the slice
 * @param sliceData The actual data in the slice
 * @returns Expected maximum size in bytes
 */
function estimateMaxExpectedSize(
  sliceName: string,
  initialSize: number,
  sliceData: any
): number {
  // Set reasonable maximums based on our app's observed usage patterns
  if (sliceName === "songs" || sliceName.includes("song")) {
    return Math.max(initialSize, 400 * 1024); // Maintained at 400 KB for songs
  } else if (sliceName === "api") {
    return Math.max(initialSize, 700 * 1024); // Increased to 700 KB based on observed usage
  } else if (sliceName === "auth") {
    return Math.max(initialSize, 10 * 1024); // Maintained at 10 KB for auth
  } else {
    // For other slices, use a reasonable multiplier but cap at 100 KB
    return Math.min(initialSize * 5, 100 * 1024);
  }
}

/**
 * Try to load persisted baselines from localStorage
 * @returns Whether valid baselines were loaded
 */
function tryLoadPersistedBaselines(): boolean {
  try {
    // Skip if we don't have localStorage (e.g., in SSR environment)
    if (typeof localStorage === "undefined") return false;

    const stored = localStorage.getItem(CONFIG.STORAGE_KEY);
    if (!stored) return false;

    const data = JSON.parse(stored);

    // Validate the stored data has the expected structure
    if (
      !data.baselineTimestamp ||
      !data.initialStateSize ||
      !data.maxExpectedStateSize ||
      !data.initialSliceSizes ||
      !data.maxExpectedSliceSizes
    ) {
      // Invalid data structure, remove it
      localStorage.removeItem(CONFIG.STORAGE_KEY);
      return false;
    }

    // Check baseline version to ensure we don't use outdated baselines
    // that were created with different threshold expectations
    if (!data.version || data.version < CONFIG.BASELINE_VERSION) {
      // Baseline is from a previous threshold configuration, remove it
      localStorage.removeItem(CONFIG.STORAGE_KEY);
      return false;
    }

    // Check if the baseline is too old (reduce from 7 days to 1 hour)
    const now = Date.now();
    const ONE_HOUR = 60 * 60 * 1000;
    if (now - data.baselineTimestamp > ONE_HOUR) {
      // Baseline is stale, remove it
      localStorage.removeItem(CONFIG.STORAGE_KEY);
      return false;
    }

    // Force fresh baselines if URL has reset parameter
    if (
      typeof window !== "undefined" &&
      window.location.search.includes("reset")
    ) {
      localStorage.removeItem(CONFIG.STORAGE_KEY);
      return false;
    }

    // Load the persisted data
    monitoringState.baselineEstablished = true;
    monitoringState.baselineTimestamp = data.baselineTimestamp;
    monitoringState.initialStateSize = data.initialStateSize;
    monitoringState.maxExpectedStateSize = data.maxExpectedStateSize;
    monitoringState.initialSliceSizes = data.initialSliceSizes;
    monitoringState.maxExpectedSliceSizes = data.maxExpectedSliceSizes;

    // Format timestamp
    const timestamp = new Date(data.baselineTimestamp).toLocaleString();

    // Build readable baseline information
    const logLines = [
      `Redux State Baselines [persisted from ${timestamp}]:`,
      `- Total: ${formatSize(data.initialStateSize)} (max: ${formatSize(
        data.maxExpectedStateSize
      )})`,
    ];

    // Add slice information, comparing with hardcoded minimums
    Object.entries(data.initialSliceSizes).forEach(([sliceName, size]) => {
      // Get the hardcoded minimum for this slice (if available)
      let hardcodedMin = 0;
      if (sliceName === "songs" || sliceName.includes("song")) {
        hardcodedMin = 400 * 1024; // 400 KB for songs
      } else if (sliceName === "api") {
        hardcodedMin = 700 * 1024; // 700 KB for API
      } else if (sliceName === "auth") {
        hardcodedMin = 10 * 1024; // 10 KB for auth
      } else {
        hardcodedMin = Math.min(Number(size) * 5, 100 * 1024); // Other slices
      }

      // Calculate effective max (greater of persisted or hardcoded)
      const persistedMax = data.maxExpectedSliceSizes[sliceName];
      const effectiveMax = Math.max(persistedMax, hardcodedMin);

      // Add to log lines
      logLines.push(
        `- ${sliceName}: ${formatSize(Number(size))} ` +
          (hardcodedMin !== effectiveMax
            ? `(min: ${formatSize(hardcodedMin)}, `
            : `(`) +
          `max: ${formatSize(effectiveMax)})`
      );
    });

    // Log formatted baseline information to browser console (for developer debugging)
    console.log(logLines.join("\n"));

    return true;
  } catch (e) {
    // On any error, clear the stored data
    localStorage.removeItem(CONFIG.STORAGE_KEY);
    logger.warn("Failed to load persisted baselines, cleared storage", e);
    return false;
  }
}

/**
 * Persist current baselines to localStorage
 */
function persistBaselines(): void {
  try {
    // Skip if we don't have localStorage or if baselines aren't established
    if (
      typeof localStorage === "undefined" ||
      !monitoringState.baselineEstablished
    )
      return;

    const now = Date.now();

    // Don't persist too frequently
    if (
      monitoringState.lastPersistTime &&
      now - monitoringState.lastPersistTime < CONFIG.PERSIST_INTERVAL
    ) {
      return;
    }

    const dataToStore = {
      baselineTimestamp: monitoringState.baselineTimestamp,
      initialStateSize: monitoringState.initialStateSize,
      maxExpectedStateSize: monitoringState.maxExpectedStateSize,
      initialSliceSizes: monitoringState.initialSliceSizes,
      maxExpectedSliceSizes: monitoringState.maxExpectedSliceSizes,
      version: CONFIG.BASELINE_VERSION, // Store current version with the data
    };

    localStorage.setItem(CONFIG.STORAGE_KEY, JSON.stringify(dataToStore));
    monitoringState.lastPersistTime = now;
  } catch (e) {
    logger.warn("Failed to persist baselines", e);
  }
}

/**
 * Establish initial baselines based on current state
 * @param store Redux store
 */
function establishBaselines(store: Store): void {
  // Check if we already have baselines or if we can load persisted ones
  if (monitoringState.baselineEstablished || tryLoadPersistedBaselines()) {
    return;
  }

  const state = store.getState();
  const now = Date.now();

  // Measure total state size
  const totalSize = measureObjectSize(state);

  // Measure per-slice sizes and estimate expected maximums
  const initialSliceSizes: Record<string, number> = {};
  const maxExpectedSliceSizes: Record<string, number> = {};
  let totalExpectedMax = 0;

  // Process each slice of state
  Object.entries(state).forEach(([sliceName, sliceData]) => {
    const sliceSize = measureObjectSize(sliceData);
    initialSliceSizes[sliceName] = sliceSize;

    // Estimate reasonable maximum size based on content
    const maxExpectedSize = estimateMaxExpectedSize(
      sliceName,
      sliceSize,
      sliceData
    );
    maxExpectedSliceSizes[sliceName] = maxExpectedSize;
    totalExpectedMax += maxExpectedSize;
  });

  // Store the baseline data
  monitoringState.baselineEstablished = true;
  monitoringState.baselineTimestamp = now;
  monitoringState.initialStateSize = totalSize;
  monitoringState.maxExpectedStateSize = totalExpectedMax;
  monitoringState.initialSliceSizes = initialSliceSizes;
  monitoringState.maxExpectedSliceSizes = maxExpectedSliceSizes;

  // Add the initial measurement to history
  monitoringState.measurementHistory.push({
    timestamp: now,
    totalSize,
    sliceSizes: { ...initialSliceSizes },
  });

  // Format timestamp
  const timestamp = new Date(now).toLocaleString();

  // Build readable baseline information
  const logLines = [
    `Redux State Baselines [established ${timestamp}]:`,
    `- Total: ${formatSize(totalSize)} (max: ${formatSize(totalExpectedMax)})`,
  ];

  // Add slice information with hardcoded minimum information
  Object.entries(initialSliceSizes).forEach(([sliceName, size]) => {
    // Get the hardcoded minimum for this slice
    let hardcodedMin = 0;
    if (sliceName === "songs" || sliceName.includes("song")) {
      hardcodedMin = 400 * 1024; // 400 KB for songs
    } else if (sliceName === "api") {
      hardcodedMin = 700 * 1024; // 700 KB for API
    } else if (sliceName === "auth") {
      hardcodedMin = 10 * 1024; // 10 KB for auth
    } else {
      hardcodedMin = Math.min(size * 5, 100 * 1024); // Other slices
    }

    const actualMax = maxExpectedSliceSizes[sliceName];

    // Add to log lines
    logLines.push(
      `- ${sliceName}: ${formatSize(size)} ` +
        (hardcodedMin !== actualMax
          ? `(min: ${formatSize(hardcodedMin)}, `
          : `(`) +
        `max: ${formatSize(actualMax)})`
    );
  });

  // Log formatted baseline information to browser console (for developer debugging)
  console.log(logLines.join("\n"));

  // Persist the baselines
  persistBaselines();
}

/**
 * Analyze the current state size and detect potential issues
 * @param store Redux store
 */
function analyzeStateSize(store: Store): void {
  // Skip analysis if baselines aren't established
  if (!monitoringState.baselineEstablished) return;

  const state = store.getState();
  const now = Date.now();

  // Measure current sizes
  const totalSize = measureObjectSize(state);
  const sliceSizes: Record<string, number> = {};

  Object.entries(state).forEach(([sliceName, sliceData]) => {
    sliceSizes[sliceName] = measureObjectSize(sliceData);
  });

  // Get the previous measurement for comparison
  const prevMeasurement =
    monitoringState.measurementHistory.length > 0
      ? monitoringState.measurementHistory[
          monitoringState.measurementHistory.length - 1
        ]
      : null;

  // Calculate growth since last measurement
  const growth = prevMeasurement
    ? (totalSize - prevMeasurement.totalSize) / prevMeasurement.totalSize
    : 0;

  const timeSinceLast = prevMeasurement
    ? (now - prevMeasurement.timestamp) / 1000 // seconds
    : 0;

  // Calculate growth rate (% per second)
  const growthRate = timeSinceLast > 0 ? growth / timeSinceLast : 0;

  // Add current measurement to history
  monitoringState.measurementHistory.push({
    timestamp: now,
    totalSize,
    sliceSizes: { ...sliceSizes },
  });

  // Trim history if needed
  if (monitoringState.measurementHistory.length > CONFIG.MAX_HISTORY_ITEMS) {
    monitoringState.measurementHistory.shift();
  }

  // Check for issues based on multiple factors
  let hasIssue = false;
  const issues: string[] = [];

  // 1. Check if total size exceeds absolute threshold
  if (totalSize > CONFIG.ABSOLUTE_SIZE_WARNING) {
    hasIssue = true;
    issues.push(
      `Total state size ${formatSize(
        totalSize
      )} exceeds warning threshold of ${formatSize(
        CONFIG.ABSOLUTE_SIZE_WARNING
      )}`
    );
  }

  // 2. Check if total size exceeds expected maximum
  if (totalSize > monitoringState.maxExpectedStateSize) {
    hasIssue = true;
    issues.push(
      `Total state size ${formatSize(
        totalSize
      )} exceeds expected maximum of ${formatSize(
        monitoringState.maxExpectedStateSize
      )}`
    );
  }

  // 3. Check for rapid growth since last measurement
  if (prevMeasurement && growth > CONFIG.RAPID_GROWTH_THRESHOLD) {
    hasIssue = true;
    issues.push(
      `State grew by ${(growth * 100).toFixed(1)}% in ${timeSinceLast.toFixed(
        0
      )} seconds`
    );
  }

  // If we found issues, prepare a more detailed report
  if (hasIssue) {
    // Identify problematic slices
    const problematicSlices = Object.entries(sliceSizes)
      .map(([sliceName, size]) => {
        const initialSize = monitoringState.initialSliceSizes[sliceName] || 0;
        const maxExpectedSize =
          monitoringState.maxExpectedSliceSizes[sliceName] || initialSize * 5;
        const prevSize = prevMeasurement?.sliceSizes[sliceName] || initialSize;

        const growthFromInitial =
          initialSize > 0 ? (size - initialSize) / initialSize : 1;
        const growthFromPrev = prevSize > 0 ? (size - prevSize) / prevSize : 0;

        return {
          name: sliceName,
          size,
          initialSize,
          maxExpectedSize,
          growthFromInitial,
          growthFromPrev,
          exceedsExpected: size > maxExpectedSize,
          percentOfTotal: totalSize > 0 ? size / totalSize : 0,
        };
      })
      .filter(
        (slice) =>
          slice.exceedsExpected ||
          slice.growthFromPrev > CONFIG.SIGNIFICANT_GROWTH_THRESHOLD ||
          slice.percentOfTotal > 0.25 // Significant portion of total state
      )
      .sort(
        (a, b) =>
          // Sort by most concerning first: exceeds expected, then by growth, then by size
          (b.exceedsExpected ? 1 : 0) - (a.exceedsExpected ? 1 : 0) ||
          b.growthFromPrev - a.growthFromPrev ||
          b.size - a.size
      );

    // Determine if this is a critical issue that should be shown even with CRITICAL_WARNINGS_ONLY
    const isCriticalIssue =
      // Extremely rapid growth (2x the already high threshold)
      growth > CONFIG.RAPID_GROWTH_THRESHOLD * 2 ||
      // Approaching absolute size limit (80%+)
      totalSize > CONFIG.ABSOLUTE_SIZE_WARNING * 0.8 ||
      // Any slice far exceeding its expected size (1.5x+)
      problematicSlices.some(
        (slice) => slice.size > slice.maxExpectedSize * 1.5
      ) ||
      // Any slice with extremely rapid growth
      problematicSlices.some(
        (slice) => slice.growthFromPrev > CONFIG.RAPID_GROWTH_THRESHOLD * 2
      );

    // Skip non-critical warnings when CRITICAL_WARNINGS_ONLY is true
    if (CONFIG.CRITICAL_WARNINGS_ONLY && !isCriticalIssue) {
      // Still record the issue in the debug log without showing warnings
      logger.debug(
        `Suppressed non-critical Redux state size warning: ${issues.join("; ")}`
      );

      // Track suppressed warnings count in localStorage for visibility
      try {
        const key = "redux_suppressed_warnings_count";
        const currentCount = parseInt(localStorage.getItem(key) || "0", 10);
        localStorage.setItem(key, (currentCount + 1).toString());
      } catch (e) {
        // Ignore storage errors
      }

      return; // Exit early without showing warnings
    }

    // If we reach here, either CRITICAL_WARNINGS_ONLY is false or this is a critical issue

    // Add a note about critical issues
    const warningPrefix = isCriticalIssue ? "CRITICAL " : "";

    // Log the warning with detailed information
    logger.warn(
      `${warningPrefix}Redux State Size Warning: ${issues.join("; ")}`
    );

    if (problematicSlices.length > 0) {
      logger.warn("Problematic state slices:");
      problematicSlices.forEach((slice) => {
        logger.warn(
          `- ${slice.name}: ${formatSize(slice.size)} ` +
            `(${(slice.percentOfTotal * 100).toFixed(1)}% of total, ` +
            `${
              slice.exceedsExpected ? "EXCEEDS" : "within"
            } expected max of ${formatSize(slice.maxExpectedSize)}, ` +
            `${slice.growthFromPrev > 0 ? "+" : ""}${(
              slice.growthFromPrev * 100
            ).toFixed(1)}% since last check)`
        );
      });

      // Add some targeted recommendations based on the problematic slices
      const recommendations: string[] = [];

      problematicSlices.forEach((slice) => {
        if (slice.name.includes("api") || slice.name.includes("cache")) {
          recommendations.push(
            `Consider implementing cache expiration for "${slice.name}" to limit its size`
          );
        } else if (
          slice.name.includes("song") ||
          slice.name.includes("track") ||
          slice.name.includes("playlist")
        ) {
          recommendations.push(
            `Consider implementing pagination or virtualization for "${slice.name}" instead of storing all items in Redux`
          );
        } else if (slice.name.includes("ui") || slice.name.includes("form")) {
          recommendations.push(
            `Check if UI state in "${slice.name}" should be moved to local component state instead of Redux`
          );
        }
      });

      // Add general recommendations
      if (recommendations.length === 0) {
        recommendations.push(
          "Check for duplicate data or unnecessary caching in Redux state"
        );
        recommendations.push(
          "Consider moving large datasets to component state or context if they aren't needed globally"
        );
        recommendations.push(
          "Implement data pagination and cleanup of stale data"
        );
      }

      logger.warn("Recommendations:");
      recommendations.forEach((rec, i) => {
        logger.warn(`${i + 1}. ${rec}`);
      });

      // Add tip about seeing all warnings if a critical issue was found
      if (isCriticalIssue && CONFIG.CRITICAL_WARNINGS_ONLY) {
        logger.warn(
          "To see all Redux state warnings, set REDUX_SHOW_ALL_WARNINGS=true in your .env file"
        );
      }
    }

    // Adaptively adjust expectations if needed
    if (
      problematicSlices.length > 0 &&
      totalSize > monitoringState.maxExpectedStateSize
    ) {
      // Check if this might be a legitimate need for more state
      const timeSinceBaseline =
        (now - monitoringState.baselineTimestamp) / (1000 * 60 * 60 * 24); // days

      // If it's been a while since baselines were established and growth isn't too rapid,
      // we can gradually adapt our expectations
      if (timeSinceBaseline > 1 && growthRate < 0.01) {
        // Increase expected size by 20%, but no more than doubling initial expectation
        const newMax = Math.min(
          monitoringState.maxExpectedStateSize * 1.2,
          monitoringState.initialStateSize * 10 // Hard limit at 10x initial
        );

        logger.info(
          `Adapting state size expectations: ${formatSize(
            monitoringState.maxExpectedStateSize
          )} → ${formatSize(newMax)}`
        );

        monitoringState.maxExpectedStateSize = newMax;

        // Also adapt slice expectations proportionally
        problematicSlices.forEach((slice) => {
          if (slice.exceedsExpected) {
            const newSliceMax = Math.min(
              monitoringState.maxExpectedSliceSizes[slice.name] * 1.2,
              slice.initialSize * 10 // Hard limit at 10x initial
            );
            monitoringState.maxExpectedSliceSizes[slice.name] = newSliceMax;
          }
        });

        // Persist updated baselines
        persistBaselines();
      }
    }
  } else {
    // If no issues, sometimes log normal status for visibility
    const daysSinceBaseline =
      (now - monitoringState.baselineTimestamp) / (1000 * 60 * 60 * 24);
    if (daysSinceBaseline >= 1 && Math.random() < 0.1) {
      // ~10% chance, once per day
      logger.debug(
        `Redux state size normal: ${formatSize(totalSize)} ` +
          `(${(
            (totalSize / monitoringState.maxExpectedStateSize) *
            100
          ).toFixed(1)}% of max expected)`
      );
    }
  }
}

/**
 * Start monitoring Redux state size
 * @param store Redux store
 * @returns A function to stop monitoring
 */
export function startReduxSizeMonitoring(store: Store): () => void {
  // Skip monitoring entirely if disabled globally
  if (!MonitoringConfig.ENABLE_REDUX_SIZE_MONITORING) {
    return () => {}; // Return a no-op cleanup function
  }

  // Clear any existing interval
  if (monitoringState.intervalHandler !== null) {
    clearInterval(monitoringState.intervalHandler);
    monitoringState.intervalHandler = null;
  }

  // Establish baselines after a short delay (to let initial state settle)
  setTimeout(() => {
    establishBaselines(store);
  }, CONFIG.INITIAL_BASELINE_DELAY);

  // Set up regular checking interval
  monitoringState.intervalHandler = window.setInterval(() => {
    analyzeStateSize(store);

    // Occasionally persist baselines
    if (Math.random() < 0.2) {
      // ~20% chance per check
      persistBaselines();
    }
  }, CONFIG.CHECK_INTERVAL);

  // Return a function to stop monitoring
  return () => {
    if (monitoringState.intervalHandler !== null) {
      clearInterval(monitoringState.intervalHandler);
      monitoringState.intervalHandler = null;
    }
  };
}

/**
 * Reset baselines and start fresh monitoring
 * This is useful if you want to force a reestablishment of baselines
 * @param store Redux store
 */
export function resetReduxSizeMonitoring(store: Store): void {
  // Clear existing monitoring
  if (monitoringState.intervalHandler !== null) {
    clearInterval(monitoringState.intervalHandler);
    monitoringState.intervalHandler = null;
  }

  // Reset monitoring state
  monitoringState = {
    baselineEstablished: false,
    baselineTimestamp: 0,
    initialStateSize: 0,
    maxExpectedStateSize: 0,
    initialSliceSizes: {},
    maxExpectedSliceSizes: {},
    measurementHistory: [],
    lastPersistTime: 0,
    intervalHandler: null,
  };

  // Try to clean up persisted data
  try {
    if (typeof localStorage !== "undefined") {
      // Clear all redux-related data
      Object.keys(localStorage).forEach((key) => {
        if (key.includes("redux") || key.includes("persist")) {
          console.log("Clearing:", key);
          localStorage.removeItem(key);
        }
      });

      // Specifically clear our monitoring baselines
      localStorage.removeItem("redux_state_size_baselines");
    }
  } catch (e) {
    // Ignore errors
  }

  // Restart monitoring
  startReduxSizeMonitoring(store);
}

/**
 * Get current monitoring status (for debugging or reporting)
 * @returns Object with monitoring state information
 */
export function getReduxSizeMonitoringStatus(): {
  enabled: boolean;
  baselineEstablished: boolean;
  baselineAge: number;
  currentSizes: {
    total: number;
    percentOfMax: number;
    measurements: number;
  };
} {
  const enabled = monitoringState.intervalHandler !== null;
  const baselineEstablished = monitoringState.baselineEstablished;
  const baselineAge =
    monitoringState.baselineTimestamp > 0
      ? (Date.now() - monitoringState.baselineTimestamp) / (1000 * 60 * 60 * 24) // days
      : 0;

  const latestMeasurement =
    monitoringState.measurementHistory.length > 0
      ? monitoringState.measurementHistory[
          monitoringState.measurementHistory.length - 1
        ]
      : null;

  const currentSizes = {
    total: latestMeasurement?.totalSize || 0,
    percentOfMax:
      latestMeasurement && monitoringState.maxExpectedStateSize > 0
        ? (latestMeasurement.totalSize / monitoringState.maxExpectedStateSize) *
          100
        : 0,
    measurements: monitoringState.measurementHistory.length,
  };

  return {
    enabled,
    baselineEstablished,
    baselineAge,
    currentSizes,
  };
}

/**
 * Get detailed information about the current baselines (for debugging)
 * @returns Detailed baseline information with formatted sizes
 */
export function getDetailedBaselines(): {
  established: boolean;
  timestamp: string;
  initialStateSize: string;
  maxExpectedStateSize: string;
  slices: Array<{
    name: string;
    initialSize: string;
    maxExpectedSize: string;
  }>;
} {
  return {
    established: monitoringState.baselineEstablished,
    timestamp: new Date(monitoringState.baselineTimestamp).toLocaleString(),
    initialStateSize: formatSize(monitoringState.initialStateSize),
    maxExpectedStateSize: formatSize(monitoringState.maxExpectedStateSize),
    slices: Object.entries(monitoringState.initialSliceSizes).map(
      ([name, size]) => ({
        name,
        initialSize: formatSize(size),
        maxExpectedSize: formatSize(
          monitoringState.maxExpectedSliceSizes[name]
        ),
      })
    ),
  };
}

/**
 * Get the count of suppressed warnings since the counter was last reset
 * Useful for adding a small indicator in development tools
 * @returns The number of suppressed warnings
 */
export function getSuppressedWarningsCount(): number {
  try {
    if (typeof localStorage === "undefined") return 0;
    const count = parseInt(
      localStorage.getItem("redux_suppressed_warnings_count") || "0",
      10
    );
    return isNaN(count) ? 0 : count;
  } catch (e) {
    return 0;
  }
}

/**
 * Reset the suppressed warnings counter
 * Call this after reviewing suppressed warnings or when starting a new session
 */
export function resetSuppressedWarningsCount(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("redux_suppressed_warnings_count", "0");
    }
  } catch (e) {
    // Ignore errors
  }
}

export default {
  startReduxSizeMonitoring,
  resetReduxSizeMonitoring,
  getReduxSizeMonitoringStatus,
  getDetailedBaselines,
  getSuppressedWarningsCount,
  resetSuppressedWarningsCount,
};
